import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { formatDate, formatAmount, parseBalanceStatus } from "@/lib/format";
import type {
  FriendRequestView,
  MonthlySpendingPoint,
  FriendSpendingItem,
  DashboardSummary,
} from "@/lib/types";
import {
  loadDashboard,
  acceptDashboardRequest,
  rejectDashboardRequest,
} from "@/lib/server-fns/dashboard";
import {
  Heading,
  Button,
  HStack,
  Card,
  Text,
  VStack,
  Badge,
  SimpleGrid,
  Stat,
  Box,
  Flex,
} from "@chakra-ui/react";
import { Chart, useChart } from "@chakra-ui/charts";
import { BarList, type BarListData } from "@chakra-ui/charts";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
} from "recharts";
import { UserNameCell } from "@/components/user-name-cell";
import { AmountBadge } from "@/components/amount-badge";
import { showErrorToast } from "@/lib/toast-utils";
import { getUser, getDisplayName } from "@/lib/user-utils";
import { LuArrowLeftRight, LuCheck, LuX, LuArrowRight, LuInfo } from "react-icons/lu";
import { Tooltip } from "@/components/ui/tooltip";
import { AddExpenseButton } from "@/components/add-expense-button";

export const Route = createFileRoute("/_authenticated/dashboard")({
  loader: () => loadDashboard(),
  component: Dashboard,
});

const CURRENCY_COLORS: Record<string, string> = {
  CAD: "teal.solid",
  USD: "blue.solid",
  EUR: "purple.solid",
  GBP: "orange.solid",
};

function MonthlySpendingChart({
  data,
  currencies,
}: {
  data: MonthlySpendingPoint[];
  currencies: string[];
}) {
  const chart = useChart({
    data,
    series: currencies.map((c) => ({
      name: c,
      color: CURRENCY_COLORS[c] ?? "teal.solid",
      stackId: "spending",
    })),
  });

  return (
    <Chart.Root maxH="sm" chart={chart}>
      <BarChart data={chart.data}>
        <CartesianGrid stroke={chart.color("border.muted")} vertical={false} />
        <XAxis axisLine={false} tickLine={false} dataKey={chart.key("label")} />
        <YAxis
          axisLine={false}
          tickLine={false}
          tickFormatter={chart.formatNumber({
            style: "currency",
            currency: currencies[0] ?? "USD",
            notation: "compact",
          })}
        />
        <RechartsTooltip
          cursor={{ fill: chart.color("bg.muted") }}
          animationDuration={100}
          content={<Chart.Tooltip />}
        />
        {currencies.length > 1 && <Legend content={<Chart.Legend />} />}
        {chart.series.map((item) => (
          <Bar
            key={item.name}
            isAnimationActive={false}
            dataKey={chart.key(item.name)}
            fill={chart.color(item.color)}
            stackId={item.stackId}
            radius={item === chart.series[chart.series.length - 1] ? [4, 4, 0, 0] : undefined}
          />
        ))}
      </BarChart>
    </Chart.Root>
  );
}

function FriendSpendingList({ data }: { data: FriendSpendingItem[] }) {
  const chart = useChart<BarListData>({
    sort: { by: "value", direction: "desc" },
    data: data.map((d) => ({ name: d.name, value: d.value })),
    series: [{ name: "name", color: "teal.subtle" }],
  });

  const currency = data[0]?.currency ?? "USD";

  return (
    <BarList.Root chart={chart}>
      <BarList.Content>
        <BarList.Bar />
        <BarList.Value
          valueFormatter={(value) => formatAmount(String(value), currency, { absolute: true })}
        />
      </BarList.Content>
    </BarList.Root>
  );
}

function ThisMonthStats({ summary }: { summary: DashboardSummary[] }) {
  if (summary.length === 0) return null;

  return (
    <HStack gap={{ base: "4", md: "8" }} flexWrap="wrap">
      {summary.map((s) => (
        <HStack key={s.currency} gap={{ base: "4", md: "8" }} flexWrap="wrap">
          <Stat.Root>
            <Stat.Label>{s.currency} Expenses</Stat.Label>
            <Stat.ValueText>{formatAmount(s.expenseTotal, s.currency)}</Stat.ValueText>
            <Stat.HelpText>
              {s.expenseCount} expense{s.expenseCount !== 1 ? "s" : ""}
            </Stat.HelpText>
          </Stat.Root>
          <Stat.Root>
            <Stat.Label>{s.currency} Settlements</Stat.Label>
            <Stat.ValueText>{formatAmount(s.settlementTotal, s.currency)}</Stat.ValueText>
            <Stat.HelpText>
              {s.settlementCount} settlement{s.settlementCount !== 1 ? "s" : ""}
            </Stat.HelpText>
          </Stat.Root>
        </HStack>
      ))}
    </HStack>
  );
}

function Dashboard() {
  const {
    expenses,
    settlements,
    balances,
    totals,
    friends,
    incoming,
    currentUser,
    monthlySpending,
    spendingCurrencies,
    friendSpending,
    summary,
  } = Route.useLoaderData();
  const router = useRouter();
  const ctx = { currentUser, friends };

  const getRequestDisplayName = (req: FriendRequestView): string => {
    return req.fromUser?.name || req.fromUser?.email || req.fromUserId;
  };

  const handleAccept = async (requestId: string) => {
    try {
      await acceptDashboardRequest({ data: { requestId } });
      router.invalidate();
    } catch (error) {
      showErrorToast("Action failed", error);
    }
  };

  const handleReject = async (requestId: string) => {
    try {
      await rejectDashboardRequest({ data: { requestId } });
      router.invalidate();
    } catch (error) {
      showErrorToast("Action failed", error);
    }
  };

  const firstName = currentUser.name ? currentUser.name.split(" ")[0] : currentUser.email;

  return (
    <>
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        mb="6"
        gap="3"
      >
        <Heading as="h2" size="lg">
          Welcome back, {firstName}
        </Heading>
        <Flex gap="2" direction={{ base: "column", sm: "row" }}>
          <AddExpenseButton />
          <Button asChild variant="outline">
            <Link to="/settlements/new">
              <LuArrowLeftRight size={16} />
              Settle Up
            </Link>
          </Button>
        </Flex>
      </Flex>

      {totals.length > 0 && (
        <Card.Root variant="outline" mb="6">
          <Card.Body p="4">
            <HStack gap={{ base: "4", md: "8" }} flexWrap="wrap">
              {totals.map((total, index) => {
                const { num, status } = parseBalanceStatus(total.amount);
                return (
                  <Stat.Root key={index}>
                    <Stat.Label>{total.currency} Balance</Stat.Label>
                    <Stat.ValueText
                      color={
                        status === "zero"
                          ? "fg.muted"
                          : status === "positive"
                            ? "green.fg"
                            : "red.fg"
                      }
                    >
                      {num > 0 ? "+" : ""}
                      {formatAmount(total.amount, total.currency)}
                    </Stat.ValueText>
                    <Stat.HelpText>
                      {status === "zero"
                        ? "All settled up"
                        : status === "positive"
                          ? "owed to you"
                          : "you owe"}
                    </Stat.HelpText>
                  </Stat.Root>
                );
              })}
            </HStack>
          </Card.Body>
        </Card.Root>
      )}

      {(summary.length > 0 || monthlySpending.length > 0) && (
        <Card.Root variant="outline" mb="6">
          <Card.Header pb="2">
            <HStack gap="2">
              <Heading as="h3" size="md">
                This Month
              </Heading>
              <Tooltip
                content="Your expense and settlement totals for the current month, plus a 6-month spending history based on your share of each expense."
                showArrow
              >
                <Box as="span" color="fg.muted" cursor="default" display="inline-flex">
                  <LuInfo size={14} />
                </Box>
              </Tooltip>
            </HStack>
          </Card.Header>
          <Card.Body pt="0" p="4">
            <VStack align="stretch" gap="6">
              <ThisMonthStats summary={summary} />
              {monthlySpending.length > 0 && (
                <MonthlySpendingChart data={monthlySpending} currencies={spendingCurrencies} />
              )}
            </VStack>
          </Card.Body>
        </Card.Root>
      )}

      <SimpleGrid columns={{ base: 1, md: 2 }} gap="4">
        {/* Recent Expenses */}
        <Card.Root variant="outline">
          <Card.Header pb="2">
            <HStack justify="space-between">
              <Heading as="h3" size="md">
                Recent Expenses
              </Heading>
              <Button asChild variant="plain" size="sm">
                <Link to="/expenses">View all</Link>
              </Button>
            </HStack>
          </Card.Header>
          <Card.Body pt="0" p="4">
            {expenses.length === 0 ? (
              <VStack align="center" gap="2" py="8">
                <Text fontSize="sm" color="fg.muted">
                  No expenses yet
                </Text>
              </VStack>
            ) : (
              <VStack align="stretch" gap="0">
                {expenses.map((expense, index) => (
                  <Box
                    key={expense.id}
                    asChild
                    py="2.5"
                    borderBottomWidth={index < expenses.length - 1 ? "1px" : "0"}
                    _hover={{ bg: "bg.subtle" }}
                    px="1"
                    borderRadius="sm"
                  >
                    <Link
                      to="/expenses/$id"
                      params={{ id: expense.id }}
                      style={{ textDecoration: "none" }}
                    >
                      <HStack justify="space-between">
                        <VStack align="start" gap="0">
                          <Text fontWeight="medium" fontSize="sm">
                            {expense.description}
                          </Text>
                          <Text fontSize="xs" color="fg.muted">
                            {formatDate(expense.date)} &middot; Paid by{" "}
                            {getDisplayName(expense.paidBy, ctx)}
                          </Text>
                        </VStack>
                        <Text fontWeight="medium" fontSize="sm" whiteSpace="nowrap">
                          {formatAmount(expense.amount, expense.currency, { absolute: false })}
                        </Text>
                      </HStack>
                    </Link>
                  </Box>
                ))}
              </VStack>
            )}
          </Card.Body>
        </Card.Root>

        {/* Balances by Friend */}
        <Card.Root variant="outline">
          <Card.Header pb="2">
            <HStack justify="space-between">
              <Heading as="h3" size="md">
                Balances
              </Heading>
              <Button asChild variant="plain" size="sm">
                <Link to="/expenses">View all</Link>
              </Button>
            </HStack>
          </Card.Header>
          <Card.Body pt="0" p="4">
            {balances.length === 0 ? (
              <VStack align="center" gap="2" py="8">
                <Text fontSize="sm" color="fg.muted">
                  No balances yet
                </Text>
              </VStack>
            ) : (
              <VStack align="stretch" gap="0">
                {balances.map((balance, index) => {
                  const user = getUser(balance.userId, ctx);
                  const identifier = user.email || balance.userId;

                  return (
                    <HStack
                      key={index}
                      justify="space-between"
                      py="2.5"
                      borderBottomWidth={index < balances.length - 1 ? "1px" : "0"}
                      px="1"
                    >
                      <UserNameCell
                        name={user.name}
                        identifier={identifier}
                        displayName={getDisplayName(balance.userId, ctx)}
                      />
                      <AmountBadge amount={balance.amount} currency={balance.currency} showSign />
                    </HStack>
                  );
                })}
              </VStack>
            )}
          </Card.Body>
        </Card.Root>

        {/* Recent Settlements */}
        <Card.Root variant="outline">
          <Card.Header pb="2">
            <HStack justify="space-between">
              <Heading as="h3" size="md">
                Recent Settlements
              </Heading>
              <Button asChild variant="plain" size="sm">
                <Link to="/expenses">View all</Link>
              </Button>
            </HStack>
          </Card.Header>
          <Card.Body pt="0" p="4">
            {settlements.length === 0 ? (
              <VStack align="center" gap="2" py="8">
                <Text fontSize="sm" color="fg.muted">
                  No settlements yet
                </Text>
              </VStack>
            ) : (
              <VStack align="stretch" gap="0">
                {settlements.map((settlement, index) => (
                  <HStack
                    key={settlement.id}
                    justify="space-between"
                    py="2.5"
                    borderBottomWidth={index < settlements.length - 1 ? "1px" : "0"}
                    px="1"
                  >
                    <HStack gap="2" fontSize="sm" minW="0" overflow="hidden">
                      <Text fontWeight="medium" truncate>
                        {getDisplayName(settlement.fromUserId, ctx)}
                      </Text>
                      <Box as="span" color="fg.muted" display="inline-flex" flexShrink={0}>
                        <LuArrowRight size={14} />
                      </Box>
                      <Text fontWeight="medium" truncate>
                        {getDisplayName(settlement.toUserId, ctx)}
                      </Text>
                    </HStack>
                    <VStack align="end" gap="0">
                      <Badge colorPalette="green" variant="subtle">
                        {formatAmount(settlement.amount, settlement.currency, { absolute: false })}
                      </Badge>
                      <Text fontSize="xs" color="fg.muted">
                        {formatDate(settlement.date)}
                      </Text>
                    </VStack>
                  </HStack>
                ))}
              </VStack>
            )}
          </Card.Body>
        </Card.Root>

        {/* Friend Requests */}
        <Card.Root variant="outline">
          <Card.Header pb="2">
            <HStack justify="space-between">
              <HStack gap="2">
                <Heading as="h3" size="md">
                  Friend Requests
                </Heading>
                {incoming.length > 0 && (
                  <Badge colorPalette="orange" variant="solid">
                    {incoming.length}
                  </Badge>
                )}
              </HStack>
              <Button asChild variant="plain" size="sm">
                <Link to="/friends">View all</Link>
              </Button>
            </HStack>
          </Card.Header>
          <Card.Body pt="0" p="4">
            {incoming.length === 0 ? (
              <VStack align="center" gap="2" py="8">
                <Text fontSize="sm" color="fg.muted">
                  No pending requests
                </Text>
              </VStack>
            ) : (
              <VStack align="stretch" gap="0">
                {incoming.map((req, index) => {
                  const name = getRequestDisplayName(req);
                  const email = req.fromUser?.email || "";

                  return (
                    <HStack
                      key={req.id}
                      justify="space-between"
                      py="2.5"
                      borderBottomWidth={index < incoming.length - 1 ? "1px" : "0"}
                      px="1"
                    >
                      <UserNameCell
                        name={req.fromUser?.name || ""}
                        identifier={email || req.fromUserId}
                        displayName={name}
                        fontWeight="medium"
                      />
                      <HStack gap="1">
                        <Button size="xs" colorPalette="green" onClick={() => handleAccept(req.id)}>
                          <LuCheck size={14} />
                          Accept
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorPalette="red"
                          onClick={() => handleReject(req.id)}
                        >
                          <LuX size={14} />
                          Reject
                        </Button>
                      </HStack>
                    </HStack>
                  );
                })}
              </VStack>
            )}
          </Card.Body>
        </Card.Root>
      </SimpleGrid>

      {friendSpending.length > 0 && (
        <Card.Root variant="outline" mt="4">
          <Card.Header pb="2">
            <HStack gap="2">
              <Heading as="h3" size="md">
                Top Shared Expenses
              </Heading>
              <Tooltip
                content="Friends you share the most expenses with, ranked by your total split amount across all shared expenses."
                showArrow
              >
                <Box as="span" color="fg.muted" cursor="default" display="inline-flex">
                  <LuInfo size={14} />
                </Box>
              </Tooltip>
            </HStack>
          </Card.Header>
          <Card.Body pt="0" p="4">
            <FriendSpendingList data={friendSpending} />
          </Card.Body>
        </Card.Root>
      )}
    </>
  );
}
